import {css} from '@emotion/react';

// in pixels
const small = 768;
const medium = 900;
const large = 1061;
const minIncrement = 1;

function max<T extends number>(value: T) {
  return `max-width: ${value - minIncrement}px`;
}
const min = <T extends number>(val: T) => `min-width: ${val}px`;

const majorBreakpoints = {
  mediumAndDown: `(${max(medium)})`,
  mediumAndUp: `(${min(medium)})`,
  largeAndUp: `(${min(large)})`,
} as const;

const minorBreakpoints = {
  smallAndDown: `(${max(small)})`,
  smallToMedium: `(${min(small)}) and (${max(medium)})`,
  smallAndUp: `(${min(small)})`,
} as const;

const allBreakpoints = {
  ...majorBreakpoints,
  ...minorBreakpoints,
} as const;

type Breakpoints = typeof allBreakpoints;

export const breakpointValues = {
  mediumAndDown: 899,
  mediumAndUp: 900,
  largeAndUp: 1061,
  smallAndDown: 767,
  smallToMedium: {min: 768, max: 899},
  smallAndUp: 768,
};

/**
 * Given some breakpoint value,
 * return a
 * @param value
 * @returns
 */
function createBreakpoint<K extends keyof Breakpoints>(value: Breakpoints[K]) {
  return function breakpoint<T extends Parameters<typeof css>>(...args: T) {
    return css`
      @media screen and ${value} {
        ${css(...args)};
      }
    `;
  };
}

type BreakpointObject = {
  -readonly [K in keyof Breakpoints]: ReturnType<typeof createBreakpoint>;
};

function hasBreakpoint(key: string): key is keyof BreakpointObject {
  return key in allBreakpoints;
}

/**
 * @deprecated - this should be solved by recipe.
 */
const breakpoints = Object.entries(allBreakpoints).reduce(
  (acc, [key, value]) => {
    if (hasBreakpoint(key)) {
      acc[key] = createBreakpoint(value);
    }
    return acc;
  },
  // eslint-disable-next-line
  {} as BreakpointObject
);

export default breakpoints;
